import { useEffect, useRef, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Palette, Buildings, CloudArrowUp, Rocket, CheckCircle } from "@phosphor-icons/react";

const BRAND_COLORS = ["#0f172a", "#00C4CC", "#ef4444", "#f97316", "#eab308", "#22c55e", "#3b82f6", "#8b5cf6", "#ec4899"];

export default function TenantBranding() {
  const [t, setT] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [f, setF] = useState({ name: "", brand_color: "#0f172a", location_gate_radius_miles: 0.5 });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/tenant");
      setT(data);
      setLoadError(null);
      setF({
        name: data.name || "",
        brand_color: data.brand_color || "#0f172a",
        location_gate_radius_miles: typeof data.location_gate_radius_miles === "number" ? data.location_gate_radius_miles : 0.5,
      });
    } catch (e) {
      setLoadError(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Network error");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/tenant", {
        name: f.name.trim() || undefined,
        brand_color: f.brand_color,
        location_gate_radius_miles: f.location_gate_radius_miles,
      });
      toast.success("Settings saved");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Logo must be under 3 MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/tenant/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!t) return <AdminShell><div className="p-8 font-bold">{loadError ? <span className="text-red-600">Failed to load company info — {loadError}</span> : "Loading..."}</div></AdminShell>;

  const logoUrl = t.logo_path ? `${API}/tenant/logo/${t.id}?_=${Date.now()}` : null;
  const isPro = t.plan === "pro";
  const nearLimit = false;  // no worker cap anymore — kept as unused for layout compatibility

  return (
    <AdminShell>
      <div className="p-6 sm:p-8 max-w-4xl space-y-6">
        <div>
          <h1 className="font-display font-black text-4xl tracking-tight">Company Branding</h1>
          <p className="mt-1 text-slate-500 font-medium">Your logo, name, and brand color appear on the app + every PDF work order you send.</p>
        </div>

        {/* Plan card */}
        <section className={`rugged-card p-6 ${nearLimit ? "border-amber-500 bg-amber-50" : ""}`} data-testid="tenant-plan-card">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs font-black uppercase tracking-widest text-slate-500">Current Plan</div>
              <div className="mt-1 flex items-center gap-3">
                <span className={`px-3 py-1 font-display font-black text-xl uppercase ${t.plan === "pro" ? "bg-[#00C4CC] text-white" : t.plan === "trial" ? "bg-gradient-to-r from-violet-600 to-[#00C4CC] text-white" : "bg-slate-200 text-slate-800"}`}>
                  {t.plan}
                </span>
                {t.ai_enabled && <CheckCircle weight="fill" size={26} className="text-[#00C4CC]" title="AI features unlocked"/>}
              </div>
              {t.plan === "trial" && t.trial_ends_at && (
                <div className="mt-2 text-sm font-bold text-violet-700" data-testid="trial-ends">
                  Trial ends {new Date(t.trial_ends_at).toLocaleDateString()} — {Math.max(0, Math.ceil((new Date(t.trial_ends_at) - Date.now()) / 86400000))} days left
                </div>
              )}
              {!t.ai_enabled && (
                <div className="mt-2 text-sm font-bold text-red-700">
                  ⚠ AI features locked. Upgrade to Pro to re-enable the Estimator, scanners, auto-tagging, and voice tools.
                </div>
              )}
            </div>
            {t.plan !== "pro" && (
              <div className="text-right">
                <div className="text-xs font-bold uppercase text-slate-500 tracking-wider mb-1">Ready for full Pro?</div>
                <a href="mailto:andrewharman84@gmail.com?subject=Upgrade%20to%20Pro"
                  className="tactile-btn inline-flex items-center gap-2 h-11 px-4 bg-[#00C4CC] text-white font-black uppercase text-xs tracking-wider"
                  data-testid="tenant-upgrade-cta">
                  <Rocket weight="bold" size={16}/> Contact to Upgrade
                </a>
              </div>
            )}
          </div>
        </section>

        {/* Logo */}
        <section className="rugged-card p-6" data-testid="tenant-logo-section">
          <h2 className="font-display font-black text-xl uppercase flex items-center gap-2">
            <CloudArrowUp weight="bold" size={22}/> Company Logo
          </h2>
          <p className="mt-1 text-sm text-slate-500 font-medium">Shown on your dashboard sidebar and every PDF work order. PNG/JPEG, max 3 MB.</p>
          <div className="mt-4 flex items-center gap-6 flex-wrap">
            <div className="h-24 w-24 border-2 border-dashed border-[#0f172a] bg-slate-50 flex items-center justify-center">
              {logoUrl ? <img src={logoUrl} alt="logo" className="max-h-20 max-w-20 object-contain" data-testid="tenant-logo-preview"/> : <span className="text-xs text-slate-400 font-bold">NO LOGO</span>}
            </div>
            <div className="flex gap-2">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp"
                onChange={(e) => uploadLogo(e.target.files?.[0])}
                data-testid="tenant-logo-input" className="hidden"/>
              <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="tenant-logo-upload-btn"
                className="tactile-btn h-11 px-4 bg-[#0f172a] text-white font-black uppercase text-xs tracking-wider disabled:opacity-50">
                {t.logo_path ? "Replace Logo" : "Upload Logo"}
              </button>
            </div>
          </div>
        </section>

        {/* Name & color */}
        <section className="rugged-card p-6" data-testid="tenant-name-color-section">
          <h2 className="font-display font-black text-xl uppercase flex items-center gap-2">
            <Buildings weight="bold" size={22}/> Name &amp; Color
          </h2>
          <div className="mt-4 space-y-4">
            <div>
              <label className="text-xs font-black uppercase tracking-wider">Company Name</label>
              <input value={f.name} onChange={e => setF({ ...f, name: e.target.value })}
                data-testid="tenant-name-input"
                className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a]"/>
            </div>
            <div>
              <label className="text-xs font-black uppercase tracking-wider flex items-center gap-1"><Palette size={14}/> Brand Color</label>
              <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="tenant-color-picker">
                {BRAND_COLORS.map(c => (
                  <button key={c} onClick={() => setF({ ...f, brand_color: c })}
                    data-testid={`tenant-color-${c.replace('#','')}`}
                    className={`h-10 w-10 border-4 ${f.brand_color === c ? "border-[#0f172a] scale-110" : "border-white shadow"}`}
                    style={{ background: c }} title={c}/>
                ))}
                <input type="color" value={f.brand_color} onChange={e => setF({ ...f, brand_color: e.target.value })}
                  data-testid="tenant-color-custom"
                  className="h-10 w-14 border-2 border-[#0f172a] cursor-pointer"/>
                <div className="ml-2 text-xs font-mono font-bold">{f.brand_color.toUpperCase()}</div>
              </div>
            </div>
          </div>
        </section>

        <section className="rugged-card p-5" data-testid="field-discipline-section">
          <h2 className="font-display font-black text-xl uppercase flex items-center gap-2">
            <Buildings weight="bold" size={22}/> Field Discipline
          </h2>
          <div className="mt-4 space-y-4">
            <div>
              <label className="text-xs font-black uppercase tracking-wider" htmlFor="loc-radius-input">
                Clock-in Location Radius (miles)
              </label>
              <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                Techs must be within this radius of the property to clock in.
                Bump higher for gated complexes or campuses (1.0), tighten for tight urban (0.25).
                Set to <b>0</b> to disable the distance check entirely (GPS still required).
              </p>
              <div className="mt-2 flex items-center gap-3">
                <input
                  id="loc-radius-input"
                  data-testid="location-gate-radius-input"
                  type="number" min="0" max="10" step="0.05"
                  value={f.location_gate_radius_miles}
                  onChange={e => setF({ ...f, location_gate_radius_miles: Math.max(0, Math.min(10, parseFloat(e.target.value) || 0)) })}
                  className="w-32 h-11 px-3 border-2 border-[#0f172a] text-lg font-black text-center"/>
                <input
                  type="range" min="0" max="2" step="0.05"
                  value={f.location_gate_radius_miles}
                  onChange={e => setF({ ...f, location_gate_radius_miles: parseFloat(e.target.value) })}
                  data-testid="location-gate-radius-slider"
                  className="flex-1 accent-[#00C4CC]"/>
                <div className="text-xs font-black uppercase tracking-wider w-14 text-right">
                  {f.location_gate_radius_miles === 0 ? "OFF" : `${Math.round(f.location_gate_radius_miles * 1609)} m`}
                </div>
              </div>
              <div className="mt-2 flex gap-2 flex-wrap">
                {[
                  { v: 0,    label: "OFF" },
                  { v: 0.25, label: "TIGHT (400 m)" },
                  { v: 0.5,  label: "DEFAULT (800 m)" },
                  { v: 1.0,  label: "COMPLEXES (1.6 km)" },
                  { v: 2.0,  label: "RURAL (3.2 km)" },
                ].map(p => (
                  <button key={p.v} type="button"
                    onClick={() => setF({ ...f, location_gate_radius_miles: p.v })}
                    data-testid={`location-radius-preset-${p.v}`}
                    className={`h-8 px-2.5 border-2 border-[#0f172a] text-[10px] font-black uppercase tracking-widest ${
                      Math.abs(f.location_gate_radius_miles - p.v) < 0.01
                        ? "bg-[#0f172a] text-white"
                        : "bg-white hover:bg-slate-100"
                    }`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <div>
          <button onClick={save} disabled={busy} data-testid="tenant-save-btn"
            className="tactile-btn h-12 px-8 bg-[#00C4CC] text-white font-black uppercase tracking-wider disabled:opacity-50">
            {busy ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </div>
    </AdminShell>
  );
}
